import { useEffect, useRef, useState } from 'react';
import api from 'api';
import striptags from 'striptags';
import { useDispatch, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getCourseMergeabilityErrorMessage } from 'lib/courses';
import { wikiLangCodes } from 'lib/lang';
import { capitalizeString } from 'shared-helpers/common';
import { Alert, Button, ModalButtonWithForm } from 'components/common';
import { FormGroup, RichText, Select, Text } from 'components/common/form';

import type { FormApi, FormValues } from 'informed';
import type { Course } from 'shared-types';

interface Props {
  onMergeSuccess?: () => void;
}

const SettingsMergeLanguageVersion = ({ onMergeSuccess }: Props) => {
  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const { dispatch } = useDispatch();

  const formApi = useRef<FormApi>(undefined);

  const [courseCompatibilityValidating, setCourseCompatibilityValidating] =
    useState<boolean>(false);
  const [courseCompatibilityValidated, setCourseCompatibilityValidated] =
    useState<boolean>(false);
  const [courseCompatibilityResultText, setCourseCompatibilityResultText] =
    useState<string>('');
  const [validatedSourceCourseId, setValidatedSourceCourseId] = useState<
    number | null
  >(null);

  const { data: coursesData } = useApiData<{
    courses: Pick<Course, 'id' | 'slug' | 'title'>[];
  }>({
    url: '/admin/courses-administered?permission=edit-content'
  });

  const courseOptions =
    coursesData?.courses
      ?.filter((listCourse) => listCourse.id !== course.id)
      .map((listCourse) => ({
        label: listCourse.title,
        value: listCourse.id
      })) || [];

  const validateMergeCompatibility = async (sourceCourseId: number) => {
    try {
      setCourseCompatibilityValidating(true);

      const res = await api.get(
        `/admin/courses/${course.id}/merge-compatibility/${sourceCourseId}`
      );
      const result = res.data.mergeValidationResultMessage;
      const isSuccess = result === 'success';

      setCourseCompatibilityValidated(isSuccess);
      if (isSuccess) setValidatedSourceCourseId(sourceCourseId);

      if (!isSuccess) {
        const { code, placeholders } = result;

        setCourseCompatibilityResultText(
          getCourseMergeabilityErrorMessage({
            mergeabilityErrorCode: code,
            messages,
            placeholders
          }) || ''
        );
      } else {
        setCourseCompatibilityResultText('');
      }
      setCourseCompatibilityValidating(false);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      setCourseCompatibilityValidating(false);
      setCourseCompatibilityValidated(false);
    }
  };

  // pre-fill title field with selected course title
  useEffect(() => {
    if (
      !courseCompatibilityValidated ||
      !validatedSourceCourseId ||
      !coursesData?.courses ||
      !formApi
    )
      return;

    const selectedCourseTitle = coursesData.courses.find(
      (listCourse) => listCourse.id === validatedSourceCourseId
    )?.title;

    if (selectedCourseTitle) {
      formApi.current.setValue('title', selectedCourseTitle);
    }
  }, [
    courseCompatibilityValidated,
    coursesData?.courses,
    formApi,
    validatedSourceCourseId
  ]);

  const handleSubmit = async (values: FormValues) => {
    try {
      await api.put(`/admin/courses/${course.id}/add-language-version`, {
        ...values,
        sourceCourseId: Number(values?.sourceCourseId)
      });

      const courses = await api.get(`/admin/courses?id=${course.id}`);

      setCourseCompatibilityValidated(false);
      if (onMergeSuccess) onMergeSuccess();

      dispatch.updatePageState({
        course: courses.data.courses[0]
      });

      dispatch.addNotice({
        text: messages.courseLanguageVersionsAddSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="file-import"
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={messages.courseLanguageVersionsMergeImportFromCourse}
      modalActionText={messages.commonImport}
      modalTitle={messages.courseLanguageVersionsMergeImportFromCourse}
      modalWidth="medium"
      setFormApi={(informedApi: FormApi) => (formApi.current = informedApi)}
      onHide={() => {
        setCourseCompatibilityValidated(false);
        setCourseCompatibilityResultText('');
        setValidatedSourceCourseId(null);
      }}
      onSubmit={handleSubmit}
      pristineIsValid={false}
      submitDisabled={!courseCompatibilityValidated}
    >
      {({ values }) => (
        <div>
          <Alert
            text={
              <>
                <p>{messages.courseLanguageVersionsMergeCourseInfo}</p>
                <p>{messages.courseLanguageVersionsMergeCourseInfoNote}</p>
              </>
            }
          />

          <FormGroup htmlFor="sourceCourseId" label={messages.commonCourse}>
            <Select
              disabled={courseCompatibilityValidated}
              field="sourceCourseId"
              id="sourceCourseId"
              isRequired={true}
              isSearchable={true}
              options={courseOptions}
              style={{ maxWidth: 300 }}
            />
          </FormGroup>

          {!courseCompatibilityValidated ? (
            <>
              <Button
                disabled={!values?.sourceCourseId}
                loading={courseCompatibilityValidating}
                onClick={() =>
                  validateMergeCompatibility(Number(values?.sourceCourseId))
                }
                style={{ marginTop: 10 }}
                text={
                  messages.courseLanguageVersionsMergeCourseValidateCompatibility
                }
              />
              {courseCompatibilityResultText && (
                <Alert
                  style={{ marginTop: 15 }}
                  text={courseCompatibilityResultText}
                  title={messages.courseLanguageVersionsMergeNotPossible}
                  type="error-light"
                />
              )}
            </>
          ) : (
            <>
              <Alert
                style={{ marginTop: 15 }}
                text={messages.courseLanguageVersionsMergeIsPossible}
                type="success-light"
              />

              <FormGroup htmlFor="language" label={messages.commonLanguage}>
                <Select
                  field="language"
                  id="language"
                  isRequired={true}
                  isSearchable={true}
                  options={wikiLangCodes
                    .filter(
                      (opt) =>
                        opt.value !== course.language &&
                        !Object.keys(course.languageVersions || {}).includes(
                          opt.value
                        )
                    )
                    .map((option) => ({
                      label: capitalizeString(option.label),
                      value: option.value
                    }))}
                  style={{ maxWidth: 300 }}
                />
              </FormGroup>
              <FormGroup htmlFor="title" label={messages.coursesCourseTitle}>
                <Text
                  field="title"
                  id="title"
                  placeholder={course.title}
                  required={true}
                />
              </FormGroup>
              <FormGroup
                htmlFor="description"
                label={messages.coursesCourseDescription}
              >
                <RichText
                  autoGrow={false}
                  field="description"
                  id="description"
                  maxLength={3000}
                  placeholder={striptags(course.description || '')}
                />
              </FormGroup>
            </>
          )}
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default SettingsMergeLanguageVersion;
